import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import './ExportReport.css';
import * as XLSX from "xlsx-js-style";
import * as turf from "@turf/turf";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Download, DownloadCloud, Edit, RefreshCcw, Share } from 'lucide-react';
import { baseurl } from '../../baseurl';
import L from 'leaflet';

import Swal from 'sweetalert2';

// ── Module-level helpers for map capture ────────────────────────────────────
// Tile bitmaps are cached across exports so repeated exports are fast.
const MAP_TILE_CACHE = (window.__measroTileCache = window.__measroTileCache || new Map());

const loadTileBitmap = (url) => {
    if (!MAP_TILE_CACHE.has(url)) {
        MAP_TILE_CACHE.set(url, (async () => {
            try {
                const res = await fetch(url, { mode: 'cors' });
                if (!res.ok) return null;
                return await createImageBitmap(await res.blob());
            } catch (e) {
                return null;
            }
        })());
    }
    return MAP_TILE_CACHE.get(url);
};

const loadImageEl = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
});

const ExportReport = ({
    property,
    zones          = [],
    polygons       = [],
    lines          = [],
    points         = [],
    layerRefs,
    editableLayers,
    projectDetails,
    proid,
}) => {
    const [isModalOpen,  setIsModalOpen]  = useState(false);
    const [isExporting,  setIsExporting]  = useState(false);
    const [exportStatus, setExportStatus] = useState('');
    const [format,       setFormat]       = useState('pdf');

    const userToken = localStorage.getItem('userToken');

    const getSidebarValue = (groupName, sectionSelector) => {
        // Try group-header-row first (polygons/lines/points)
        let rows = document.querySelectorAll(`${sectionSelector} .group-header-row`);
        for (const row of rows) {
            const nameEl = row.querySelector('.feature-name');
            if (nameEl?.textContent?.trim().startsWith(groupName)) {
                const valueEl = row.querySelector('.feature-value');
                return parseFloat(valueEl?.textContent?.replace(/,/g, '') || '0');
            }
        }

        // Fallback: plain <tr> rows (used by ReportZone)
        rows = document.querySelectorAll(`${sectionSelector} tr`);
        for (const row of rows) {
            const nameEl = row.querySelector('.feature-name');
            if (nameEl?.textContent?.trim() === groupName) {
                const valueEl = row.querySelector('.feature-value');
                return parseFloat(valueEl?.textContent?.replace(/,/g, '') || '0');
            }
        }

        return 0;
    };

    console.log("zones", zones);

    // ── REPORT ID ─────────────────────────────────────────────────────────────
    const generateAndSaveReportId = async (exportFormat) => {
        try {
            const response = await fetch(`${baseurl}reports/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
                body: JSON.stringify({
                    property_id:  property?.id              || null,
                    project_id:   proid                     || null,
                    format:       exportFormat,
                    address:      projectDetails?.address   || property?.address || '',
                    report_type:  projectDetails?.reportType || '',
                }),
            });
            const data = await response.json();
            if (data.success) return data.data.report_id;
            return `RPT-${Date.now()}`;
        } catch { return `RPT-${Date.now()}`; }
    };

    // ── DATA HELPERS ──────────────────────────────────────────────────────────
    const groupData = (items, key) => {
        const grouped = {};
        items.forEach(item => {
            const name = item.name || 'Unknown';
            if (!grouped[name]) grouped[name] = { name, value: 0 };
            grouped[name].value += key === 'count' ? 1 : (item[key] || 0);
        });
        return Object.values(grouped);
    };

    const processPolygons = () => {
        const groups = {};
        polygons.forEach(p => {
            if (!groups[p.name]) groups[p.name] = [];
            groups[p.name].push(p);
        });
        return polygons.map(p => {
            const groupTotal = getSidebarValue(p.name, '#polygon-section, .coverage-section:not(.line-coverage-section):not(.point-coverage-section)');
            const count = groups[p.name].length;
            return { ...p, sqft: groupTotal / count };
        });
    };

    const processZones = () => {
        const groups = {};
        zones.forEach(z => {
            if (!groups[z.name]) groups[z.name] = [];
            groups[z.name].push(z);
        });
        return zones.map(z => {
            const groupTotal = getSidebarValue(z.name, '#zones-section');
            const count = groups[z.name].length;
            return { ...z, sqft: groupTotal / count };
        });
    };

    const processLines = () => {
        const groups = {};
        lines.forEach(l => {
            if (!groups[l.name]) groups[l.name] = [];
            groups[l.name].push(l);
        });
        return lines.map(l => {
            const groupTotal = getSidebarValue(l.name, '.line-coverage-section');
            const count = groups[l.name].length;
            return { ...l, feet: groupTotal / count };
        });
    };

    const sleep = ms => new Promise(r => setTimeout(r, ms));

    // ── MAP CAPTURE (canvas-based; never touches or moves the live map) ───────
    const getLeafletMap = () => {
        if (window._leafletInstance) return window._leafletInstance;
        const containers = document.querySelectorAll('.leaflet-container');
        for (const el of containers) {
            if (el._leaflet_map) return el._leaflet_map;
        }
        return null;
    };

    const getLayers = () => {
        if (layerRefs && typeof layerRefs.current === 'object' && layerRefs.current !== null) {
            return layerRefs.current;
        }
        if (layerRefs && typeof layerRefs === 'object') {
            return layerRefs;
        }
        return {};
    };

    const renderMapToImage = async ({ ids = null, includeBuffer = false, width, height, maxZoom = 19, padding = 40 }) => {
        const map = getLeafletMap();
        if (!map) return null;
        const layers = getLayers();

        // 1. Which registered features to draw
        const entries = Object.entries(layers)
            .filter(([, reg]) => reg && reg.group)
            .filter(([id, reg]) => (ids ? ids.includes(id) : map.hasLayer(reg.group)))
            .sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0));

        // 2. Dashed buffer outline (only for overview)
        let bufferLayer = null;
        if (includeBuffer) {
            map.eachLayer((l) => {
                if (l instanceof L.GeoJSON && l.options && l.options.style
                    && typeof l.options.style === 'object' && l.options.style.dashArray) {
                    bufferLayer = l;
                }
            });
        }

        // 3. Bounds to fit
        const bounds = L.latLngBounds([]);
        entries.forEach(([, reg]) => {
            const b = reg.group.getBounds ? reg.group.getBounds() : null;
            if (b && b.isValid()) bounds.extend(b);
        });
        if (bufferLayer) {
            const bb = bufferLayer.getBounds();
            if (bb && bb.isValid()) bounds.extend(bb);
        }
        const fitBounds = bounds.isValid() ? bounds : map.getBounds();

        // 4. Compute exact zoom + origin so the bounds fill the canvas
        const sw = fitBounds.getSouthWest();
        const ne = fitBounds.getNorthEast();
        const z0a = map.project(sw, 0);
        const z0b = map.project(ne, 0);
        const dx0 = Math.abs(z0b.x - z0a.x);
        const dy0 = Math.abs(z0b.y - z0a.y);
        const availW = Math.max(width - padding * 2, 10);
        const availH = Math.max(height - padding * 2, 10);
        const zx = dx0 > 1e-9 ? Math.log2(availW / dx0) : Infinity;
        const zy = dy0 > 1e-9 ? Math.log2(availH / dy0) : Infinity;
        const fz = Math.max(1, Math.min(zx, zy, maxZoom));

        const pa = map.project(sw, fz);
        const pb = map.project(ne, fz);
        const originX = (pa.x + pb.x) / 2 - width / 2;
        const originY = (pa.y + pb.y) / 2 - height / 2;
        const toPx = (lng, lat) => {
            const p = map.project(L.latLng(lat, lng), fz);
            return [p.x - originX, p.y - originY];
        };

        // 5. Canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#1e1e1e';
        ctx.fillRect(0, 0, width, height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // 6. Base map tiles (same tile layer the live map uses)
        let baseLayer = null;
        map.eachLayer((l) => {
            if (!baseLayer && l instanceof L.TileLayer && l._url) baseLayer = l;
        });

        if (baseLayer) {
            const zoomOffset = baseLayer.options.zoomOffset || 0;
            const maxTileZoom = 19;
            let lz = Math.ceil(fz);
            let tz = lz + zoomOffset;
            if (tz > maxTileZoom) {
                tz = maxTileZoom;
                lz = tz - zoomOffset;
            }
            if (tz < 0) {
                tz = 0;
                lz = tz - zoomOffset;
            }

            const tilePx = 256 * Math.pow(2, -zoomOffset) * Math.pow(2, fz - lz);
            const nTiles = Math.pow(2, tz);
            const x0 = Math.floor(originX / tilePx);
            const x1 = Math.floor((originX + width) / tilePx);
            const y0 = Math.max(0, Math.floor(originY / tilePx));
            const y1 = Math.min(nTiles - 1, Math.floor((originY + height) / tilePx));

            const subs = baseLayer.options.subdomains;
            const sub = (typeof subs === 'string' ? subs[0] : Array.isArray(subs) ? subs[0] : 'a') || 'a';

            const jobs = [];
            for (let tx = x0; tx <= x1; tx++) {
                for (let ty = y0; ty <= y1; ty++) {
                    const wrapX = ((tx % nTiles) + nTiles) % nTiles;
                    const url = L.Util.template(baseLayer._url, { x: wrapX, y: ty, z: tz, s: sub, r: '' });
                    jobs.push(
                        loadTileBitmap(url).then((bmp) => ({
                            bmp,
                            dx: tx * tilePx - originX,
                            dy: ty * tilePx - originY,
                        }))
                    );
                }
            }
            const tiles = await Promise.all(jobs);
            tiles.forEach((t) => {
                if (t.bmp) ctx.drawImage(t.bmp, t.dx, t.dy, tilePx + 1, tilePx + 1);
            });
        }

        // 7. Nearmap overlay (drawn with the same projection → always aligned)
        const nm = window._nearmapClipLayerRef;
        const overlay = nm && nm._overlay;
        if (overlay && overlay._url && overlay._bounds && map.hasLayer(overlay)) {
            const img = await loadImageEl(overlay._url);
            if (img) {
                const nw = toPx(overlay._bounds.getWest(), overlay._bounds.getNorth());
                const se = toPx(overlay._bounds.getEast(), overlay._bounds.getSouth());
                ctx.drawImage(img, nw[0], nw[1], se[0] - nw[0], se[1] - nw[1]);
            }
        }

        // 8. Vector drawing helpers
        const ls = Math.min(3, Math.max(1, width / Math.max(map.getSize().x || 1000, 1)));

        const tracePath = (ring) => {
            ring.forEach((c, i) => {
                const [x, y] = toPx(c[0], c[1]);
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
        };

        const drawPolygons = (polys, st) => {
            ctx.beginPath();
            polys.forEach((rings) => rings.forEach((ring) => { tracePath(ring); ctx.closePath(); }));
            if (st.fillOpacity > 0) {
                ctx.globalAlpha = st.fillOpacity;
                ctx.fillStyle = st.fillColor;
                ctx.fill('evenodd');
            }
            ctx.globalAlpha = st.opacity;
            ctx.strokeStyle = st.color;
            ctx.lineWidth = st.weight * ls;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.setLineDash(st.dash ? st.dash.map((d) => d * ls) : []);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
        };

        const drawLines = (lineArr, st) => {
            ctx.beginPath();
            lineArr.forEach((line) => tracePath(line));
            ctx.globalAlpha = st.opacity;
            ctx.strokeStyle = st.color;
            ctx.lineWidth = st.weight * ls;
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.setLineDash([]);
            ctx.stroke();
            ctx.globalAlpha = 1;
        };

        const drawPoint = (coord, st) => {
            const [x, y] = toPx(coord[0], coord[1]);
            ctx.beginPath();
            ctx.arc(x, y, st.radius * ls, 0, Math.PI * 2);
            ctx.globalAlpha = 1;
            ctx.fillStyle = st.color;
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1 * ls;
            ctx.setLineDash([]);
            ctx.stroke();
        };

        const drawGeometry = (geom, reg, isProperty) => {
            if (!geom) return;
            const color = reg.color || '#3388ff';
            const op = reg.opacity != null ? reg.opacity : 0.6;
            switch (geom.type) {
                case 'Point':
                    drawPoint(geom.coordinates, { color, radius: 6 });
                    break;
                case 'MultiPoint':
                    geom.coordinates.forEach((c) => drawPoint(c, { color, radius: 6 }));
                    break;
                case 'LineString':
                    drawLines([geom.coordinates], { color, weight: 3, opacity: op });
                    break;
                case 'MultiLineString':
                    drawLines(geom.coordinates, { color, weight: 3, opacity: op });
                    break;
                case 'Polygon':
                    drawPolygons([geom.coordinates], {
                        color, fillColor: color, fillOpacity: op, opacity: 1, weight: isProperty ? 2 : 1.5,
                    });
                    break;
                case 'MultiPolygon':
                    drawPolygons(geom.coordinates, {
                        color, fillColor: color, fillOpacity: op, opacity: 1, weight: isProperty ? 2 : 1.5,
                    });
                    break;
                case 'GeometryCollection':
                    (geom.geometries || []).forEach((g) => drawGeometry(g, reg, isProperty));
                    break;
                default:
                    break;
            }
        };

        // 9. Buffer outline first (below features, same as live map)
        if (bufferLayer) {
            bufferLayer.eachLayer((l) => {
                const g = l.feature && l.feature.geometry;
                if (!g) return;
                const polys = g.type === 'MultiPolygon' ? g.coordinates : g.type === 'Polygon' ? [g.coordinates] : [];
                if (polys.length) {
                    drawPolygons(polys, {
                        color: '#00e5ff', fillColor: '#00e5ff', fillOpacity: 0, opacity: 1, weight: 2, dash: [14, 10],
                    });
                }
            });
        }

        // 10. Features, lowest order first → highest order on top
        entries.forEach(([id, reg]) => {
            reg.group.eachLayer((l) => {
                const g = l.feature && l.feature.geometry;
                drawGeometry(g, reg, id === 'property-boundary');
            });
        });

        return canvas.toDataURL('image/jpeg', 0.9);
    };

    // Overview page — aspect ratio matches the PDF frame (190 × 130 mm)
    const captureFullMap = async () => {
        try {
            return await renderMapToImage({
                ids: null,
                includeBuffer: true,
                width: 1900,
                height: 1300,
                maxZoom: 19,
                padding: 50,
            });
        } catch (e) {
            console.error('Overview capture failed:', e);
            return null;
        }
    };

    // Feature pages — aspect ratio matches the PDF frame (190 × 95 mm)
    const captureMapForFeatureIds = async (ids) => {
        try {
            return await renderMapToImage({
                ids,
                includeBuffer: false,
                width: 1900,
                height: 950,
                maxZoom: 19,
                padding: 80,
            });
        } catch (e) {
            console.error('Feature capture failed:', e);
            return null;
        }
    };

    // ── PDF CONSTANTS ─────────────────────────────────────────────────────────
    const PURPLE       = [112, 48, 160];
    const DARK_GRAY    = [50,  50,  50];
    const MID_GRAY     = [110, 110, 110];
    const LIGHT_GRAY   = [231, 230, 230];
    const WHITE        = [255, 255, 255];
    const BORDER_GRAY  = [180, 180, 180];
    const PURPLE_LIGHT = [245, 240, 252];

    const drawRect = (doc, x, y, w, h, fill, stroke) => {
        if (fill)   doc.setFillColor(...fill);
        if (stroke) doc.setDrawColor(...stroke);
        else        doc.setDrawColor(...BORDER_GRAY);
        doc.rect(x, y, w, h, fill && stroke ? 'FD' : fill ? 'F' : 'S');
    };

    const drawText = (doc, text, x, y,
        { size=9, color=DARK_GRAY, bold=false, align='left', maxWidth }={}) => {
        doc.setFontSize(size);
        doc.setTextColor(...color);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        const opts = { align };
        if (maxWidth) opts.maxWidth = maxWidth;
        doc.text(String(text ?? ''), x, y, opts);
    };

    const drawTable = (doc, x, y, colWidths, headers, rows, pageH, onNewPage) => {
        const ROW_H=7, HEAD_H=8, PAD=2.5;
        let cx = x;
        headers.forEach((h, i) => {
            drawRect(doc, cx, y, colWidths[i], HEAD_H, LIGHT_GRAY, BORDER_GRAY);
            drawText(doc, h, cx+PAD, y+5.5, { size:8, bold:true });
            cx += colWidths[i];
        });
        y += HEAD_H;
        rows.forEach((row, ri) => {
            if (y+ROW_H > pageH-20) { onNewPage(); y=30; }
            const isTotal = ['TOTAL','TOTAL LENGTH','TOTAL POINTS'].includes(String(row[0]));
            const bg = isTotal ? LIGHT_GRAY : ri%2===0 ? WHITE : [248,248,248];
            let cx2=x;
            row.forEach((cell, ci) => {
                drawRect(doc, cx2, y, colWidths[ci], ROW_H, bg, BORDER_GRAY);
                drawText(doc, cell??'', cx2+PAD, y+5, { size:8, bold:isTotal, color:isTotal?DARK_GRAY:MID_GRAY });
                cx2 += colWidths[ci];
            });
            y += ROW_H;
        });
        return y;
    };

    const drawSectionLabel = (doc, label, x, y) => {
        drawText(doc, label, x, y, { size:9, bold:true, color:PURPLE });
        return y+6;
    };

    const drawMainHeader = (doc, pageW, margin, reportId) => {
        drawRect(doc, 0, 0, pageW, 22, PURPLE);
        drawText(doc, 'MEASRO', margin, 13, { size:20, bold:true, color:WHITE });
        drawText(doc, `Report ID: ${reportId}`, pageW-margin, 10, { size:9, color:WHITE, align:'right' });
        drawRect(doc, 0, 22, pageW, 8, [90,30,130]);
        drawText(doc, `Address : ${projectDetails?.address||'988 Michigan Avenue, Detroit, MI'}`, margin, 28, { size:8, color:WHITE });
        drawText(doc, `Report Type : ${projectDetails?.reportType||''}`, pageW-margin, 28, { size:8, color:WHITE, align:'right' });
        drawRect(doc, 0, 30, pageW, 7, LIGHT_GRAY);
        drawText(doc, `ID : ${proid||'N/A'}`, margin, 35.5, { size:8, bold:true });
        drawText(doc, `Date : ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'})}`, pageW-margin, 35.5, { size:8, bold:true, align:'right' });
    };

    const drawPageHeader = (doc, pageW, margin, reportId) => {
        drawRect(doc, 0, 0, pageW, 15, PURPLE);
        drawText(doc, 'MEASRO', margin, 10, { size:13, bold:true, color:WHITE });
        drawText(doc, `Report ID: ${reportId}`, pageW-margin, 10, { size:8, color:WHITE, align:'right' });
        drawRect(doc, 0, 15, pageW, 7, [90,30,130]);
        drawText(doc, `${projectDetails?.address||''}  ·  ${projectDetails?.reportType||'Report'}`, margin, 20, { size:7.5, color:WHITE });
        drawText(doc, new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'long',year:'numeric'}), pageW-margin, 20, { size:7.5, color:WHITE, align:'right' });
    };

    const drawFooter = (doc, pageH, pageW, margin) => {
        const fy = pageH-18;
        drawRect(doc, 0, fy, pageW, 18, LIGHT_GRAY);
        doc.setDrawColor(...BORDER_GRAY); doc.setLineWidth(0.3);
        doc.line(pageW/2, fy+1, pageW/2, fy+10);
        drawText(doc, 'Generated by Measro LLC', pageW/4, fy+6.5, { size:8, bold:true, align:'center' });
        drawText(doc, 'www.measro.com', pageW*3/4, fy+6.5, { size:8, bold:true, color:PURPLE, align:'center' });
        drawText(doc, 'Disclaimer: Measurements and property boundaries are estimated using aerial imagery and may not be exact. This report is for informational purposes only and is not a legal survey. Please verify measurements before use.', margin, fy+13.5, { size:6.5, color:MID_GRAY, maxWidth:pageW-margin*2 });
    };

    const drawStatCards = (doc, y, margin, contentW, lotSqft, polyTotal, lineTotal, pointTotal) => {
        const cardW = contentW/4;
        const cards = [
            { label:'Total Lot Area', v1:`${lotSqft.toLocaleString()} sq ft`,  v2:`${(lotSqft/43560).toFixed(2)} acres` },
            { label:'Polygon Area',   v1:`${polyTotal.toLocaleString()} sq ft`, v2:`${(polyTotal/43560).toFixed(2)} acres` },
            { label:'Line Length',    v1:`${lineTotal.toLocaleString()} ft`,    v2:'' },
            { label:'Total Points',   v1:`${pointTotal} pts`,                   v2:'' },
        ];
        cards.forEach((c,i) => {
            const sx = margin+i*cardW;
            drawRect(doc, sx, y, cardW, 18, i%2===0?PURPLE_LIGHT:WHITE, BORDER_GRAY);
            drawText(doc, c.label, sx+cardW/2, y+5.5,  { size:7.5, bold:true, color:PURPLE, align:'center' });
            drawText(doc, c.v1,    sx+cardW/2, y+11.5, { size:9,   bold:true, color:DARK_GRAY, align:'center' });
            if (c.v2) drawText(doc, c.v2, sx+cardW/2, y+16.5, { size:7.5, color:MID_GRAY, align:'center' });
        });
        return y+22;
    };

    const hexToRgb = (hex) => {
        const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return r ? [parseInt(r[1],16), parseInt(r[2],16), parseInt(r[3],16)] : null;
    };

    const drawFeaturePage = (doc, pageW, pageH, margin, contentW, reportId,
        featureName, featureType, mapImg, statLine1, statLine2, color) => {

        doc.addPage();
        drawPageHeader(doc, pageW, margin, reportId);
        let y = 28;

        drawRect(doc, margin, y, contentW, 10, PURPLE_LIGHT, BORDER_GRAY);

        if (color) {
            const rgb = hexToRgb(color);
            if (rgb) {
                if (featureType === 'line') {
                    doc.setDrawColor(...rgb); doc.setLineWidth(2.5);
                    doc.line(margin+4, y+5, margin+18, y+5);
                    doc.setLineWidth(0.3);
                } else if (featureType === 'point') {
                    doc.setFillColor(...rgb); doc.setDrawColor(255,255,255); doc.setLineWidth(0.5);
                    doc.circle(margin+10, y+5, 3.5, 'FD');
                    doc.setLineWidth(0.3);
                } else {
                    doc.setFillColor(...rgb); doc.setDrawColor(...rgb);
                    doc.roundedRect(margin+4, y+2.5, 14, 6, 1.5, 1.5, 'FD');
                    doc.setDrawColor(...BORDER_GRAY);
                }
            }
        }

        drawText(doc, featureName, margin+24, y+6.5, { size:10, bold:true, color:PURPLE });
        const typeLabel = featureType==='polygon' ? 'Polygon Feature' : featureType==='line' ? 'Line Feature' : featureType==='zone' ? 'Zone Coverage' : 'Point Feature';
        drawText(doc, typeLabel, pageW-margin-2, y+6.5, { size:8, color:MID_GRAY, align:'right' });
        y += 14;

        if (mapImg) {
            const mapH = 95;
            doc.addImage(mapImg, 'JPEG', margin, y, contentW, mapH);
            doc.setDrawColor(...BORDER_GRAY); doc.setLineWidth(0.4);
            doc.rect(margin, y, contentW, mapH);
            drawRect(doc, margin, y+mapH-7, contentW, 7, [20,20,20]);
            drawText(doc, `${featureName} — isolated view`, margin+3, y+mapH-1.8, { size:7, color:[200,200,200] });
            y += mapH+6;
        } else {
            drawRect(doc, margin, y, contentW, 60, [240,240,240], BORDER_GRAY);
            drawText(doc, 'Map capture unavailable', margin+contentW/2, y+32, { size:9, color:MID_GRAY, align:'center' });
            y += 66;
        }

        const stats = [statLine1, statLine2].filter(Boolean);
        const cardW = stats.length===1 ? contentW : contentW/2;
        stats.forEach((s,i) => {
            const sx = margin+i*cardW;
            drawRect(doc, sx, y, cardW, 20, i===0?PURPLE_LIGHT:WHITE, BORDER_GRAY);
            drawText(doc, s.label, sx+cardW/2, y+7,    { size:8,  bold:true, color:PURPLE,    align:'center' });
            drawText(doc, s.value, sx+cardW/2, y+15.5, { size:12, bold:true, color:DARK_GRAY, align:'center' });
        });

        drawFooter(doc, pageH, pageW, margin);
    };

    // ── MAIN PDF EXPORT ───────────────────────────────────────────────────────
    const exportToPdf = async (reportId) => {
        setIsExporting(true);

        const processedPolygons = processPolygons();
        const processedLines    = processLines();
        const processedZones    = processZones();

        const polygonGroups = groupData(processedPolygons, 'sqft');
        const lineGroups    = groupData(processedLines,    'feet');
        const pointGroups   = groupData(points,            'count');
        const zoneGroups    = groupData(processedZones,    'sqft');

        const polyTotal  = polygonGroups.reduce((s,p) => s+p.value, 0);
        const lineTotal  = lineGroups.reduce((s,l) => s+l.value, 0);
        const pointTotal = points.length;
        const zoneTotal  = zoneGroups.reduce((s,z) => s+z.value, 0);
        const lotSqft    = property?.sqft || 0;

        const doc      = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
        const pageW    = 210;
        const pageH    = 297;
        const margin   = 10;
        const contentW = pageW - margin*2;

        // ── PAGE 1: Overview ──────────────────────────────────────────────────
        setExportStatus('Capturing overview map…');
        const fullMapImg = await captureFullMap();

        drawMainHeader(doc, pageW, margin, reportId);
        let y = 43;
        y = drawStatCards(doc, y, margin, contentW, lotSqft, polyTotal, lineTotal, pointTotal);

        if (fullMapImg) {
            const mapH = 130;
            doc.addImage(fullMapImg, 'JPEG', margin, y, contentW, mapH);
            doc.setDrawColor(...PURPLE); doc.setLineWidth(0.5);
            doc.rect(margin, y, contentW, mapH);
            drawRect(doc, margin, y+mapH-8, contentW, 8, [20,20,20]);
            drawText(doc, 'Overview — All Features', margin+3, y+mapH-2.5, { size:7, color:[200,200,200] });
        }

        drawFooter(doc, pageH, pageW, margin);

        // ── PAGE 2: Summary Tables ────────────────────────────────────────────
        doc.addPage();
        drawPageHeader(doc, pageW, margin, reportId);
        y = 30;

        const halfW = contentW/2-2;
        const col2x = margin+halfW+4;
        const savedY = y;

        y = drawSectionLabel(doc, 'Polygon Features', margin, y);
        const yAfterPoly = drawTable(doc, margin, y, [halfW*0.5, halfW*0.5],
            ['Polygon Features','Area (sq ft / acres)'],
            [...polygonGroups.map(p => [p.name, `${p.value.toLocaleString()} sq ft / ${(p.value/43560).toFixed(3)} ac`]), ['TOTAL', `${polyTotal.toLocaleString()} sq ft / ${(polyTotal/43560).toFixed(3)} ac`]],
            pageH, () => { doc.addPage(); drawPageHeader(doc, pageW, margin, reportId); });

        let yzR = savedY;
        yzR = drawSectionLabel(doc, 'Zones Coverage', col2x, yzR);
        const yAfterZone = drawTable(doc, col2x, yzR, [halfW*0.5, halfW*0.5],
            ['Zones Coverage','Area (sq ft / acres)'],
            [...zoneGroups.map(z => [z.name, `${z.value.toLocaleString()} sq ft / ${(z.value/43560).toFixed(3)} ac`]), ['TOTAL', `${zoneTotal.toLocaleString()} sq ft / ${(zoneTotal/43560).toFixed(3)} ac`]],
            pageH, () => { doc.addPage(); drawPageHeader(doc, pageW, margin, reportId); });

        y = Math.max(yAfterPoly, yAfterZone)+6;
        if (y+30 > pageH-20) { doc.addPage(); drawPageHeader(doc, pageW, margin, reportId); y=30; }

        const savedY2 = y;
        y = drawSectionLabel(doc, 'Line Features', margin, y);
        const yAfterLines = drawTable(doc, margin, y, [halfW*0.62, halfW*0.38],
            ['Line Features','Length (ft)'],
            [...lineGroups.map(l => [l.name, `${l.value.toLocaleString()} ft`]), ['TOTAL LENGTH', `${lineTotal.toLocaleString()} ft`]],
            pageH, () => { doc.addPage(); drawPageHeader(doc, pageW, margin, reportId); });

        let yzR2 = savedY2;
        yzR2 = drawSectionLabel(doc, 'Point Features', col2x, yzR2);
        drawTable(doc, col2x, yzR2, [halfW*0.62, halfW*0.38],
            ['Point Features','Count'],
            [...pointGroups.map(p => [p.name, p.value]), ['TOTAL POINTS', pointTotal]],
            pageH, () => { doc.addPage(); drawPageHeader(doc, pageW, margin, reportId); });

        drawFooter(doc, pageH, pageW, margin);

        // ── Color lookup ──────────────────────────────────────────────────────
        const colorLookup = {};
        [...polygons, ...lines, ...points].forEach(item => {
            if (!colorLookup[item.name]) colorLookup[item.name] = item.color;
        });
        const zoneColorLookup = {};
        zones.forEach(z => { if (!zoneColorLookup[z.name]) zoneColorLookup[z.name] = z.color || '#3498db'; });

        // ── INDIVIDUAL FEATURE PAGES ──────────────────────────────────────────

        // 1. Zone Groups
        for (const group of zoneGroups) {
            setExportStatus(`Capturing: ${group.name} (zone)…`);
            const groupItems = processedZones.filter(z => z.name === group.name);
            const ids = groupItems.map(z => z.id);
            const mapImg = await captureMapForFeatureIds(ids);
            drawFeaturePage(
                doc, pageW, pageH, margin, contentW, reportId,
                group.name, 'zone', mapImg,
                { label: 'Total Area', value: `${group.value.toLocaleString()} sq ft` },
                { label: 'Acres', value: `${(group.value / 43560).toFixed(3)} acres` },
                zoneColorLookup[group.name]
            );
        }

        // 2. Polygon Groups
        for (const group of polygonGroups) {
            setExportStatus(`Capturing: ${group.name} (polygon)…`);
            const groupItems = processedPolygons.filter(p => p.name === group.name);
            const ids = groupItems.map(p => p.id);
            const mapImg = await captureMapForFeatureIds(ids);
            drawFeaturePage(
                doc, pageW, pageH, margin, contentW, reportId,
                group.name, 'polygon', mapImg,
                { label: 'Total Area', value: `${group.value.toLocaleString()} sq ft` },
                { label: 'Acres', value: `${(group.value / 43560).toFixed(3)} acres` },
                colorLookup[group.name]
            );
        }

        // 3. Line Groups
        for (const group of lineGroups) {
            setExportStatus(`Capturing: ${group.name} (line)…`);
            const groupItems = processedLines.filter(l => l.name === group.name);
            const ids = groupItems.map(l => l.id);
            const mapImg = await captureMapForFeatureIds(ids);
            drawFeaturePage(
                doc, pageW, pageH, margin, contentW, reportId,
                group.name, 'line', mapImg,
                { label: 'Total Length', value: `${group.value.toLocaleString()} ft` },
                { label: 'Segments', value: `${groupItems.length}` },
                colorLookup[group.name]
            );
        }

        // 4. Point Groups
        for (const group of pointGroups) {
            setExportStatus(`Capturing: ${group.name} (point)…`);
            const groupItems = points.filter(p => p.name === group.name);
            const ids = groupItems.map(p => p.id);
            const mapImg = await captureMapForFeatureIds(ids);
            drawFeaturePage(
                doc, pageW, pageH, margin, contentW, reportId,
                group.name, 'point', mapImg,
                { label: 'Total Count', value: `${group.value}` },
                null,
                colorLookup[group.name]
            );
        }

        setExportStatus('Saving PDF…');
        doc.save(`Report_${reportId}.pdf`);
        setIsExporting(false);
        setExportStatus('');
    };

    // ── EXCEL EXPORT ──────────────────────────────────────────────────────────
    const exportToExcel = (reportId) => {
        const processedPolygons = processPolygons();
        const processedLines    = processLines();
        const processedZones    = processZones();

        const zoneGroups    = groupData(processedZones,    'sqft');
        const polygonGroups = groupData(processedPolygons, 'sqft');
        const lineGroups    = groupData(processedLines,    'feet');
        const pointGroups   = groupData(points,            'count');
        const zoneTotal  = zoneGroups.reduce((s,z)=>s+z.value,0);
        const polyTotal  = polygonGroups.reduce((s,p)=>s+p.value,0);
        const lineTotal  = lineGroups.reduce((s,l)=>s+l.value,0);
        const pointTotal = points.length;

        const wsData = [
            ['MEASRO','','',`Report ID: ${reportId}`],
            [`${projectDetails?.address||''}`, '','',`${projectDetails?.reportType||''}`],
            [`${proid||'N/A'}`, '','', new Date().toLocaleDateString()],
            [],
            ['Total Lot Area','Polygon Area','Line Length','Total Points'],
            [`${(property?.sqft||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} sq ft`,`${parseFloat(polyTotal).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} sq ft`,`${parseFloat(lineTotal).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} ft`,`${pointTotal} pts`],
            [`${((property?.sqft||0)/43560).toFixed(2)} acres`,`${(polyTotal/43560).toFixed(2)} acres`,'',''],
            [],
            ['Zones Coverage','Area (sq ft)','Polygon Features','Area (sq ft)'],
        ];
        const maxZP = Math.max(zoneGroups.length, polygonGroups.length);
        for (let i=0; i<maxZP; i++) {
            wsData.push([
                zoneGroups[i]?.name    ||'', zoneGroups[i]?.value    ? zoneGroups[i].value.toLocaleString()    +' sq ft':'',
                polygonGroups[i]?.name ||'', polygonGroups[i]?.value ? polygonGroups[i].value.toLocaleString() +' sq ft':'',
            ]);
        }
        wsData.push(['TOTAL',zoneTotal.toLocaleString()+' sq ft','TOTAL',polyTotal.toLocaleString()+' sq ft']);
        wsData.push([]);
        wsData.push(['Line Features','Length (ft)','Point Features','Count']);
        const maxLP = Math.max(lineGroups.length, pointGroups.length);
        for (let i=0; i<maxLP; i++) {
            wsData.push([
                lineGroups[i]?.name  ||'', lineGroups[i]?.value  ? lineGroups[i].value.toLocaleString()  +' ft':'',
                pointGroups[i]?.name ||'', pointGroups[i]?.value ||'',
            ]);
        }
        wsData.push(['TOTAL LENGTH',lineTotal.toLocaleString()+' ft','TOTAL POINTS',pointTotal]);
        wsData.push([]);
        wsData.push(['Generated by Measro LLC','','www.measro.com','']);
        wsData.push(['Disclaimer: Measurements and property boundaries are estimated using aerial imagery and may not be exact. This report is for informational purposes only and is not a legal survey. Please verify measurements before use.','','','']);

        const disclaimerRowIndex = wsData.length-1;
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws['!cols'] = [{wch:30},{wch:22},{wch:28},{wch:30}];
        ws['!rows'] = []; ws['!rows'][0]={hpt:28}; ws['!rows'][disclaimerRowIndex]={hpt:50};
        ws['!merges'] = [
            {s:{r:0,c:0},e:{r:0,c:2}},{s:{r:1,c:0},e:{r:1,c:2}},{s:{r:2,c:0},e:{r:2,c:2}},
            {s:{r:disclaimerRowIndex,c:0},e:{r:disclaimerRowIndex,c:3}},
        ];
        const tb = {top:{style:'thin'},bottom:{style:'thin'},left:{style:'thin'},right:{style:'thin'}};
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let R=range.s.r; R<=range.e.r; ++R) for (let C=range.s.c; C<=range.e.c; ++C) {
            const addr = XLSX.utils.encode_cell({r:R,c:C});
            if (ws[addr]) ws[addr].s = {border:tb};
        }
        if (ws['A1']) ws['A1'].s={font:{bold:true,sz:20,color:{rgb:'7030A0'}},alignment:{vertical:'center'},border:tb};
        if (ws['D1']) ws['D1'].s={font:{bold:true,sz:11,color:{rgb:'7030A0'}},alignment:{horizontal:'left',vertical:'center'},border:tb};
        if (ws['A2']) ws['A2'].s={font:{bold:true,sz:10},alignment:{vertical:'center'},border:tb};
        if (ws['D2']) ws['D2'].s={font:{italic:true,sz:9,color:{rgb:'444444'}},alignment:{horizontal:'left'},border:tb};
        if (ws['A3']) ws['A3'].s={font:{sz:9,color:{rgb:'555555'}},border:tb};
        if (ws['D3']) ws['D3'].s={font:{sz:9,color:{rgb:'555555'}},alignment:{horizontal:'left'},border:tb};
        ['A5','B5','C5','D5','A9','B9','C9','D9'].forEach(c=>{if(ws[c])ws[c].s={font:{bold:true},alignment:{horizontal:'center',vertical:'center'},fill:{fgColor:{rgb:'E7E6E6'}},border:tb};});
        Object.keys(ws).forEach(addr=>{const v=ws[addr]?.v;if(['TOTAL','TOTAL LENGTH','TOTAL POINTS'].includes(String(v)))ws[addr].s={font:{bold:true},fill:{fgColor:{rgb:'EDE7F6'}},border:tb};});
        const fA=XLSX.utils.encode_cell({r:disclaimerRowIndex-1,c:0}); if(ws[fA])ws[fA].s={font:{bold:true,sz:9},border:tb};
        const fC=XLSX.utils.encode_cell({r:disclaimerRowIndex-1,c:2}); if(ws[fC])ws[fC].s={font:{bold:true,sz:9,color:{rgb:'7030A0'}},border:tb};
        const dC=XLSX.utils.encode_cell({r:disclaimerRowIndex,c:0}); if(ws[dC])ws[dC].s={font:{sz:9,italic:true,color:{rgb:'666666'}},alignment:{wrapText:true,horizontal:'left',vertical:'top'},fill:{fgColor:{rgb:'F7F7F7'}},border:tb};
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report Summary');
        XLSX.writeFile(wb, `Report_${reportId}.xlsx`);
    };

    // ── HANDLE EXPORT ─────────────────────────────────────────────────────────
    const handleExport = async () => {
        // Force sqft/ft units in sidebar before reading values
        document.querySelectorAll('.section-unit-toggle, .total-unit-toggle').forEach(btn => {
            const unitText = btn.parentElement?.querySelector('.feature-unit, .total-unit')?.textContent?.trim();
            if (unitText && unitText !== 'sq ft' && unitText !== 'ft') {
                btn.click();
            }
        });
        await sleep(100);

        setIsModalOpen(false);
        setIsExporting(true);
        setExportStatus('Generating unique Report ID…');
        const reportId = await generateAndSaveReportId(format);
        if (format === 'excel') {
            exportToExcel(reportId);
            setIsExporting(false);
            setExportStatus('');
        } else {
            await exportToPdf(reportId);
        }
    };

    return (
        <div className="map-wrapper">
            <div className="toolbar-container">
                <button className="tool-btn" onClick={() => setIsModalOpen(true)} disabled={isExporting}>
                    <Download size={24} />
                </button>
            </div>

            {isExporting && ReactDOM.createPortal(
                <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
                    <div style={{background:'#fff',borderRadius:14,padding:'36px 48px',textAlign:'center',boxShadow:'0 8px 40px rgba(112,48,160,0.25)',minWidth:320}}>
                        <div style={{width:48,height:48,border:'4px solid #e8d9f7',borderTop:'4px solid #7030A0',borderRadius:'50%',animation:'measroSpin 0.9s linear infinite',margin:'0 auto 18px'}} />
                        <style>{`@keyframes measroSpin{to{transform:rotate(360deg)}}`}</style>
                        <div style={{fontSize:15,fontWeight:700,color:'#7030A0',marginBottom:8}}>Generating Report</div>
                        <div style={{fontSize:12,color:'#888',minHeight:20}}>{exportStatus||'Please wait…'}</div>
                    </div>
                </div>, document.body
            )}

            {isModalOpen && ReactDOM.createPortal(
                <div className="exp-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="exp-content" onClick={e => e.stopPropagation()}>
                        <h3 className="exp-title">Export GIS Report</h3>
                        <label className="exp-label">Format</label>
                        <select className="exp-select" value={format} onChange={e => setFormat(e.target.value)}>
                            <option value="pdf">PDF Document</option>
                            <option value="excel">Excel Spreadsheet</option>
                        </select>
                        <div className="exp-actions">
                            <button className="exp-btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            <button className="exp-btn-export" onClick={handleExport}>Export Now</button>
                        </div>
                    </div>
                </div>, document.body
            )}
        </div>
    );
};

export default ExportReport;